/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState, useEffect, useContext, useRef } from 'react';
import { format, isValid, parse } from 'date-fns';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { db, functions } from '../../firebase';
import { AuthContext } from '../../Providers/UserProvider';
import LoaderComponent from '../../Shared/Loader';
import { rejectionMessage } from '../../utils/rejectionMessage';
import { MISSING_COMPANY_CLAIM_ERROR, companyIdFromClaims } from '../../utils/companyClaim';

function writeAlertMessage(error: unknown, fallback: string): string {
    const detail = rejectionMessage(error, '');
    return detail ? `${fallback} ${detail}` : fallback;
}

export interface TechProject {
    createdAt: number;
    endDate: string;
    hoursLogged: number;
    projectId: string;
    startDate: string;
    techId: string;
    projectTitle: string;
    projectClient: string;
    id: string;
}

interface ProjectDetails {
    address?: {
        city: string;
        state: string;
        street: string;
        zip: string;
    } | null;
    clientName: string;
    createdAt: number;
    description: string;
    endDate: string;
    geofenceTimeCard: boolean;
    hidden: boolean;
    hoursLogged: number;
    lat: number;
    lng: number;
    maximumTechs: number;
    numberOfVehicles: string;
    skills: [];
    startDate: string;
    title: string;
    id: string;
}

/** Invalid, blank, and missing stored dates stay on the page as a placeholder. */
export function formatProjectDate(dateValue: unknown): string {
    if (typeof dateValue !== 'string' || dateValue.trim() === '') return '—';
    const parsed = parse(dateValue, 'yyyy-MM-dd', new Date());
    if (!isValid(parsed)) return '—';
    return format(parsed, 'MM/dd/yyyy');
}

/**
 * Inclusive calendar-day membership: startDate <= day <= endDate.
 * Both bounds and day are yyyy-MM-dd strings, so the end date stays current after local midnight.
 * Pass `day` when the caller already tracks the local calendar day; otherwise `now` is formatted.
 */
export function isTechOnProjectToday(
    startDate: string | undefined,
    endDate: string | undefined,
    now: Date = new Date(),
    day: string = format(now, 'yyyy-MM-dd'),
): boolean {
    if (typeof startDate !== 'string' || startDate.trim() === '') return false;
    if (typeof endDate !== 'string' || endDate.trim() === '') return false;
    return startDate <= day && day <= endDate;
}

export const PROJECT_CLOCK_OTHER_TECH = 'This project belongs to another tech.';

function nonEmptyId(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * A stored techId that does not match the signed-in tech id or uid blocks clock actions.
 * A techProject with no techId is left unchanged.
 */
export function techProjectBelongsToAnotherTech(
    techProject: { techId?: unknown } | null | undefined,
    tech: { id?: unknown; uid?: unknown } | null | undefined,
    user: { uid?: unknown } | null | undefined,
    contextTechId?: unknown,
): boolean {
    const ownerId = nonEmptyId(techProject?.techId);
    if (ownerId == null) return false;
    const signedInIds = [tech?.id, tech?.uid, user?.uid, contextTechId]
        .map(nonEmptyId)
        .filter((id): id is string => id != null);
    return !signedInIds.includes(ownerId);
}

const ProjectDetailPage = ({ match }: { match: any }) => {
    const { tech, user, techId }: any = useContext(AuthContext);
    const projectId = useMemo(() => (match.params ? match.params.projectId : null), [match]);
    const techProjectId = useMemo(() => (match.params ? match.params.techProjectId : null), [match]);
    const [techProject, setTechProject] = useState({} as TechProject);
    const [projectDetails, setProjectDetails] = useState({} as ProjectDetails);
    const [projectNotFound, setProjectNotFound] = useState(false);
    const [techProjectNotFound, setTechProjectNotFound] = useState(false);
    const [timeRecordsError, setTimeRecordsError] = useState('');
    const [timeRecordsLoaded, setTimeRecordsLoaded] = useState(false);
    const timeRecordsLoadedRef = useRef(false);
    const [projectLoadError, setProjectLoadError] = useState('');
    const [techProjectError, setTechProjectError] = useState('');
    const [isClockedIn, setIsClockedIn] = useState(false);
    /** Synchronous lock so a second click cannot clock in or out before the first await. */
    const clockLock = useRef(false);
    const [isClocking, setIsClocking] = useState(false);
    const [clockError, setClockError] = useState('');
    const [today, setToday] = useState(() => format(new Date(), 'yyyy-MM-dd'));

    const hoursToFormattedTime = (hoursLogged: number) => {
        const logged = Number.isFinite(hoursLogged) ? hoursLogged : 0;
        const totalMinutes = Math.round(logged * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    };

    useEffect(() => {
        // A failed listen stops delivering snapshots. Keep clock actions off
        // until a snapshot succeeds, and resubscribe only that query when the
        // browser comes back online.
        let alive = true;
        let failed = false;
        let generation = 0;
        let unsubscribe = () => {};
        const setRecordsLoaded = (loaded: boolean) => {
            timeRecordsLoadedRef.current = loaded;
            setTimeRecordsLoaded(loaded);
        };
        const companyId = companyIdFromClaims(user?.claims);
        if (!companyId) {
            setRecordsLoaded(false);
            setTimeRecordsError(MISSING_COMPANY_CLAIM_ERROR);
            return undefined;
        }
        const query = db
            .collection(`/regions/${user.claims.regionId}/timeRecords`)
            .where('companyId', '==', companyId)
            .where('techProjectId', '==', techProjectId)
            .where('timestampOut', '==', null);

        const subscribe = () => {
            const current = ++generation;
            unsubscribe();
            unsubscribe = query.onSnapshot(
                (snap) => {
                    if (!alive || current !== generation) return;
                    failed = false;
                    setIsClockedIn(snap.docs.length > 0);
                    setRecordsLoaded(true);
                    setTimeRecordsError('');
                },
                (error) => {
                    if (!alive || current !== generation) return;
                    failed = true;
                    console.error('Unable to load open time records', error);
                    setRecordsLoaded(false);
                    setTimeRecordsError(writeAlertMessage(error, 'Open time records could not load.'));
                },
            );
        };

        const onOnline = () => {
            if (!alive || !failed) return;
            subscribe();
        };

        setRecordsLoaded(false);
        setTimeRecordsError('');
        subscribe();
        window.addEventListener('online', onOnline);

        return () => {
            alive = false;
            unsubscribe();
            window.removeEventListener('online', onOnline);
        };
    }, [techProjectId, user]);

    useEffect(() => {
        return db
            .collection(`/regions/${user.claims.regionId}/projects`)
            .doc(`/${projectId}`)
            .onSnapshot(
                (doc) => {
                    const data = doc.data();
                    if (doc.exists === false || data === undefined || data === null) {
                        setProjectNotFound(true);
                        setProjectDetails({} as ProjectDetails);
                        setProjectLoadError('');
                        return;
                    }
                    setProjectNotFound(false);
                    setProjectLoadError('');
                    setProjectDetails({ ...data, id: doc.id } as ProjectDetails);
                },
                (error) => {
                    console.error('Unable to load project', error);
                    setProjectNotFound(false);
                    setProjectDetails({} as ProjectDetails);
                    setProjectLoadError(writeAlertMessage(error, 'Project could not load.'));
                },
            );
    }, [user, projectId]);

    useEffect(() => {
        return db
            .collection(`/regions/${user.claims.regionId}/techProjects`)
            .doc(techProjectId)
            .onSnapshot(
                (doc) => {
                    const data = doc.data();
                    if (doc.exists === false || data === undefined || data === null) {
                        setTechProjectNotFound(true);
                        setTechProject({} as TechProject);
                        return;
                    }
                    setTechProjectNotFound(false);
                    setTechProject(data as TechProject);
                    setTechProjectError('');
                },
                (error) => {
                    console.error('Unable to load tech project', error);
                    setTechProjectError(writeAlertMessage(error, 'Tech project could not load.'));
                },
            );
    }, [user, techProjectId]);

    useEffect(() => {
        let midnightTimer: ReturnType<typeof setTimeout> | undefined;

        const syncToday = () => {
            const next = format(new Date(), 'yyyy-MM-dd');
            setToday((current) => (current === next ? current : next));
        };

        const scheduleMidnight = () => {
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
            const now = new Date();
            const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
            midnightTimer = setTimeout(() => {
                syncToday();
                scheduleMidnight();
            }, nextMidnight.getTime() - now.getTime());
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            syncToday();
            scheduleMidnight();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        scheduleMidnight();

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (midnightTimer !== undefined) clearTimeout(midnightTimer);
        };
    }, []);

    const belongsToAnotherTech = techProjectBelongsToAnotherTech(techProject, tech, user, techId);

    const releaseClockLock = () => {
        clockLock.current = false;
        setIsClocking(false);
    };

    const clockIn = async () => {
        if (clockLock.current || !timeRecordsLoadedRef.current) return;
        if (techProjectBelongsToAnotherTech(techProject, tech, user, techId)) {
            return;
        }
        clockLock.current = true;
        setIsClocking(true);
        try {
            await functions.httpsCallable('clockInToProject')({
                techId: tech.id,
                regionId: user?.claims?.regionId,
                projectId,
                techProjectId,
            });
            setIsClockedIn(true);
            setClockError('');
        } catch (error) {
            console.error('Could not clock in.', error);
            setClockError(writeAlertMessage(error, 'Could not clock in.'));
        } finally {
            releaseClockLock();
        }
    };

    const clockOut = async () => {
        if (clockLock.current || !timeRecordsLoadedRef.current) return;
        if (techProjectBelongsToAnotherTech(techProject, tech, user, techId)) {
            return;
        }
        clockLock.current = true;
        setIsClocking(true);
        try {
            await functions.httpsCallable('clockOutOfProject')({
                techId: tech.id,
                regionId: user?.claims?.regionId,
                projectId,
                techProjectId,
            });
            setIsClockedIn(false);
            setClockError('');
        } catch (error) {
            console.error('Could not clock out.', error);
            setClockError(writeAlertMessage(error, 'Could not clock out.'));
        } finally {
            releaseClockLock();
        }
    };

    const formatDate = (date: string): string => formatProjectDate(date);

    const onProjectToday = useMemo(() => {
        if (!techProject) {
            return false;
        }
        return isTechOnProjectToday(techProject.startDate, techProject.endDate, new Date(), today);
    }, [techProject, today]);

    const address =
        projectDetails.address != null && typeof projectDetails.address === 'object' ? projectDetails.address : null;

    const clockInDisabled = !timeRecordsLoaded || clockLock.current || isClockedIn || isClocking;
    const clockOutDisabled = !timeRecordsLoaded || clockLock.current || !isClockedIn || isClocking;

    return (
        <div className="max-w-3/4 mx-auto">
            <LoaderComponent showLoader={!timeRecordsLoaded && !timeRecordsError} />
            {timeRecordsError ? (
                <p className="text-red-600 text-sm mt-4 text-center" role="alert" data-testid="project-time-load-error">
                    {timeRecordsError}
                </p>
            ) : null}
            {projectLoadError ? (
                <p className="text-red-600 text-sm mt-4 text-center" role="alert" data-testid="project-doc-load-error">
                    {projectLoadError}
                </p>
            ) : null}
            {techProjectError ? (
                <p className="text-red-600 text-sm mt-4 text-center" role="alert" data-testid="project-tech-load-error">
                    {techProjectError}
                </p>
            ) : null}
            {belongsToAnotherTech ? (
                <p className="text-red-600 text-sm mt-4 text-center" role="alert" data-testid="project-owner-error">
                    {PROJECT_CLOCK_OTHER_TECH}
                </p>
            ) : null}
            {onProjectToday && (
                <div className="p-4 shadow-md rounded-lg my-4 grid grid-cols-6">
                    <div className="col-span-6 xs:col-span-2 sm:col-span-3  align-middle h-min my-auto text-center py-2">
                        <p className="text-lg">
                            <span className="font-medium">Time logged by {tech.name}:</span>{' '}
                            {hoursToFormattedTime(techProject.hoursLogged)}
                        </p>
                    </div>
                    <div className="col-span-6 xs:col-span-4 sm:col-span-3 align-middle">
                        {belongsToAnotherTech ? null : (
                            <div className="flex flex-row justify-center h-full items-center">
                                <div>
                                    <button
                                        type="button"
                                        className={`mx-2 px-4 py-2 rounded-md shadow-md text-white focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-opacity-50 ${
                                            clockInDisabled
                                                ? 'bg-gray-300 hover:bg-gray-300 text-gray-50 cursor-default'
                                                : 'bg-green-500 hover:bg-green-700'
                                        }`}
                                        disabled={clockInDisabled}
                                        onClick={clockIn}
                                    >
                                        Clock in
                                    </button>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        className={`mx-2 px-4 py-2 rounded-md shadow-md text-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-opacity-50 ${
                                            clockOutDisabled
                                                ? 'bg-gray-300 hover:bg-gray-300 text-gray-50 cursor-default'
                                                : 'bg-red-500 hover:bg-red-700'
                                        }`}
                                        disabled={clockOutDisabled}
                                        onClick={clockOut}
                                    >
                                        Clock out
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {clockError ? (
                        <p
                            className="col-span-6 text-red-600 text-sm mt-4 text-center"
                            role="alert"
                            data-testid="project-clock-error"
                        >
                            {clockError}
                        </p>
                    ) : null}
                </div>
            )}
            {(projectNotFound || techProjectNotFound) ? (
                <div className="p-4 shadow-md rounded-lg my-4 text-center" data-testid="project-not-found">
                    <p className="text-xl font-medium">Project not found</p>
                </div>
            ) : null}
            {!projectNotFound && !isEmpty(projectDetails) && (
                <div className="p-4 shadow-md rounded-lg my-4">
                    <div className="text-center border-gray-100 border-b-2 pb-2">
                        <p className="text-xl font-medium">{projectDetails.title}</p>
                        <p>{projectDetails.description}</p>
                    </div>

                    <div className="pt-2">
                        <table className="mx-auto">
                            <tbody className="divide-y divide-gray-200">
                                <tr>
                                    <td className="p-4">
                                        <p className="font-medium">Address</p>
                                    </td>
                                    <td className="p-4">
                                        {address ? (
                                            <>
                                                <p>{address.street}</p>
                                                <p>
                                                    {address.city}, {address.state} {address.zip}
                                                </p>
                                            </>
                                        ) : (
                                            <p>—</p>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4">
                                        <p className="font-medium">Client</p>
                                    </td>

                                    <td className="p-4">
                                        <p>{projectDetails.clientName}</p>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4">
                                        <p className="font-medium">Dates</p>
                                    </td>
                                    <td className="p-4">
                                        <p>
                                            {formatDate(projectDetails.startDate)} -{' '}
                                            {formatDate(projectDetails.endDate)}
                                        </p>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-4">
                                        <p className="font-medium">Total time logged on project</p>
                                    </td>
                                    <td className="p-4">
                                        <p>{hoursToFormattedTime(projectDetails.hoursLogged)}</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

ProjectDetailPage.propTypes = {
    match: PropTypes.object.isRequired,
};

export default ProjectDetailPage;
